import React, {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {C, F} from './versionsStyles';

const SETTLE_MS = 600;
const GRAB_DELAYS_MS = [0, 50, 150, 300, 500];

const Input = styled.input`
	flex: 1 1 auto;
	min-width: 0;
	${F.sub2}
	color: ${C.text};
	padding: 2px 6px;
	margin: -3px 0 -3px -7px;
	border: 1px solid ${C.brand};
	border-radius: 4px;
	background: ${C.surface};
`;

export interface RenameInputProps {
	value: string;
	onChange: (value: string) => void;
	onCommit: () => void;
	onCancel: () => void;
}

/**
 * Inline version-name field: Enter or blur saves, Escape cancels. The menu that opens it hands focus back to its
 * trigger as it closes, so for the first moments the field takes focus back instead of treating the blur as a save.
 */
export const RenameInput = ({value, onChange, onCommit, onCancel}: RenameInputProps) => {
	const {formatMessage} = useIntl();
	const ref = useRef<HTMLInputElement>(null);
	const opened = useRef(Date.now());
	const settling = () => Date.now() - opened.current < SETTLE_MS;
	useEffect(() => {
		const grab = () => {
			const el = ref.current;
			if (el && document.activeElement !== el) {
				el.focus();
				el.select();
			}
		};
		const ids = GRAB_DELAYS_MS.map(d => setTimeout(() => settling() && grab(), d));
		return () => ids.forEach(clearTimeout);
	}, []);
	return (
		<Input
			ref={ref}
			value={value}
			maxLength={40}
			aria-label={formatMessage({id: 'baseline.versions.version_name'})}
			data-cy="baseline-version-rename-input"
			onClick={e => e.stopPropagation()}
			onChange={e => onChange(e.target.value)}
			onBlur={() => {
				if (settling()) {
					setTimeout(() => ref.current?.focus(), 0);
					return;
				}
				onCommit();
			}}
			onKeyDown={e => {
				e.stopPropagation();
				if (e.key === 'Enter') {
					onCommit();
				}
				if (e.key === 'Escape') {
					onCancel();
				}
			}}
		/>
	);
};
